import React from 'react';

import NotificationAddOutlinedIcon from '@mui/icons-material/NotificationAddOutlined';
import LoadingButton from '@mui/lab/LoadingButton';
import { Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';

import { useIntl } from '@kamernet/core/Intl';
import { PageLayout } from '@kamernet/core/PageLayout';

import { ListingsSearchBar } from '@kamernet/modules/Search/components/ListingsSearchBar';
import { ListingsSearchBarFilters } from '@kamernet/modules/Search/types';
import { isCountrySearch } from '@kamernet/modules/Search/utilities';

import { useSearchAlert } from '../../hooks/useSearchAlertHandling';
import { ListingsSearchContext } from '../../ListingsSearchContext';
import { AlertSnackbar } from '../AlertSnackbar';
import { SearchViewToggleButton } from '../SearchViewToggleButton';
import { SideFiltersButton } from '../SideFilters';

import styles from './AllFiltersSection.module.css';

export const AllFiltersSection = () => {
  const { T } = useIntl();
  const { applyNewBarFilters, reload, barFilters, allFilters } =
    ListingsSearchContext.useContext();

  const {
    isSavingAlert,
    saveAlert,
    searchAlertNotification,
    closeAlertSnackbar,
  } = useSearchAlert();
  const theme = useTheme();
  const isSMDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [isCityRequired, setIsCityRequired] = React.useState(false);

  const saveAlertInternal = React.useCallback(() => {
    if (isCountrySearch(allFilters)) {
      setIsCityRequired(true);
      return;
    }

    saveAlert();
  }, [allFilters, saveAlert]);

  const barFiltersChangeHandler = React.useCallback(
    (newBarFilters: ListingsSearchBarFilters) => {
      setIsCityRequired(false);

      applyNewBarFilters(newBarFilters);
    },
    [applyNewBarFilters],
  );

  return (
    <PageLayout.SectionContainer className={styles.root}>
      <div className={styles.sectionContainer}>
        <ListingsSearchBar
          filters={barFilters}
          validationMessageStrategy="always"
          className={styles.listingSearchBar}
          isCityRequired={isCityRequired}
          onChange={barFiltersChangeHandler}
          onSubmit={reload}
        />

        {isSMDown ? (
          <React.Fragment>
            <div className={styles.subSearchLine}>
              <Tooltip
                title={
                  isCountrySearch(allFilters)
                    ? T('ERR_LOCATION_IS_MISSING')
                    : ''
                }
              >
                <LoadingButton
                  variant="contained"
                  size="small"
                  className={styles.saveAlert}
                  loading={isSavingAlert}
                  onClick={saveAlertInternal}
                >
                  <NotificationAddOutlinedIcon />
                  <Typography variant="body2">
                    {T('cta.create_search_alert')}
                  </Typography>
                </LoadingButton>
              </Tooltip>

              <SideFiltersButton />
            </div>
            <SearchViewToggleButton />
          </React.Fragment>
        ) : (
          <div className={styles.subSearchLine}>
            <SearchViewToggleButton />
            <section className={styles.container}>
              <Tooltip
                title={
                  isCountrySearch(allFilters)
                    ? T('ERR_LOCATION_IS_MISSING')
                    : ''
                }
              >
                <LoadingButton
                  variant="contained"
                  className={styles.saveAlert}
                  loading={isSavingAlert}
                  onClick={saveAlertInternal}
                >
                  <NotificationAddOutlinedIcon />
                  <Typography variant="body2">
                    {T('cta.create_search_alert')}
                  </Typography>
                </LoadingButton>
              </Tooltip>

              <SideFiltersButton />
            </section>
          </div>
        )}
      </div>
      {searchAlertNotification && (
        <AlertSnackbar
          notification={searchAlertNotification}
          onClose={closeAlertSnackbar}
        />
      )}
    </PageLayout.SectionContainer>
  );
};
